import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  OutlinedInput,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
  Select,
  MenuItem,
  ListItemText,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import SimpleBar from "simplebar-react";
import { useFormik, FieldArray, FormikProvider } from "formik";
import * as Yup from "yup";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { createShippingService } from "./shippingService.helper";
import Switch from "@mui/material/Switch";
import { useSelector, useDispatch } from "react-redux";
import { setShippingServiceState, setShippingServices } from "../../../store/shippingServiceSlice";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../firebaseConfig";
import { useToastMessages } from "../../../lib/messages/useToastMessages";
import { useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";

// Utility function to get Buy-in-rate Range
function getBuyInRateRange(rates) {
  const buyIns = rates.map(r => Number(r.buyIn)).filter(v => !isNaN(v));
  if (buyIns.length === 0) return "Buy-in-rate Range $0 - $0";
  const min = Math.min(...buyIns);
  const max = Math.max(...buyIns);
  return `Buy-in-rate Range $${min} - $${max}`;
}
// Utility function to get Campaign Maturity date
function getCampaignMaturityDate(rates) {
  const min = Math.min(...rates.map(r => Number(r.minWeight)));
  return `If ${min}LB is not met, then buy-in refund applies and campaign canceled on Campaign Maturity Date (CMD)`;
}

function getRouteType(shippingServiceType, deliveryPartner, carrierPartner) {
  if (shippingServiceType === "crowdshipping") return "CrowdShipper";
  if (shippingServiceType === "traveler") return "Traveler";
  if (shippingServiceType === "standard") {
    if (deliveryPartner && carrierPartner) {
      if (deliveryPartner?.id === carrierPartner?.id) return "Direct";
      return "Forward";
    }
    return "-";
  }
  return "-";
}

export default function ShippingPriceStep({ onBack, onNext, loading }) {
  const [checked, setChecked] = useState(true);

  const handleChange = (event) => {
    setChecked(event.target.checked);
  };

  const add_customs = [
    "Delivery Duty Paid",
    "Friendly Customs",
    "Delivery Duty Unpaid",
    "Automated Customs",
    "Ship every Monday",
    "Ship every Tuesday",
    "Ship every Wednesday",
    "Ship every Thrusday",
    "Ship every Saturday",
    "Ship every Sunday",
  ];

  const [checkedCustoms, setCheckedCustoms] = useState(
    add_customs.reduce((acc, item) => ({ ...acc, [item]: false }), {})
  );

  const handleCustomsChange = (item) => (event) => {
    setCheckedCustoms((prevChecked) => ({
      ...prevChecked,
      [item]: event.target.checked,
    }));
  };

  // Add shipping partner options (example static list, replace with dynamic if needed)
  const shippingPartnerOptions = [
    { value: '', label: 'Select Shipping Partner' },
    { value: 'FedEx', label: 'FedEx' },
    { value: 'UPS', label: 'UPS' },
    { value: 'DHL', label: 'DHL' },
    { value: 'USPS', label: 'USPS' },
  ];

  const shippingServiceData = useSelector((state) => state.shippingService.shippingServiceData);
  const dispatch = useDispatch();
  const { Success, Error } = useToastMessages();
  const navigate = useNavigate();

  // Use Redux state for initialValues
  const initialValues = {
    shippingPartner: shippingServiceData.shippingPartner || '',
    shippingId: shippingServiceData.shippingId || '',
    rates: shippingServiceData.rates || [{ minWeight: '', maxWeight: '', pricePerLb: '', buyIn: '' }],
    pricePerPound: shippingServiceData.pricePerPound || '', // for standard
    maxLbPerPackage: shippingServiceData.maxLbPerPackage || '',
    minLbPerPackage: shippingServiceData.minLbPerPackage || '',
    additionalLbPerPackage: shippingServiceData.additionalLbPerPackage || '',
    addCustoms: shippingServiceData.addCustoms || [],
    baseRate: shippingServiceData.baseRate || '',
    riskSurcharge: shippingServiceData.riskSurcharge || '',
    serviceFees: shippingServiceData.serviceFees || { doorToDoorFee: '', pickUpFee: '' },
    tracking: typeof shippingServiceData.tracking === 'boolean' ? shippingServiceData.tracking : false,
    maxDimension: shippingServiceData.maxDimension || { L: '', W: '', H: '' },
    shippingServiceType: shippingServiceData.shippingServiceType || '',
    campaignMaturityDate: shippingServiceData.campaignMaturityDate || '',
    availableWeight: shippingServiceData.availableWeight || '',
  };

  // Debug: log initialValues and formik values
  console.log('initialValues:', initialValues);

  const validationSchema = Yup.object({
    shippingServiceType: Yup.string(),
    pricePerPound: Yup.string()
      .test('required-if-standard', 'Price per pound is required', function (value) {
        const { shippingServiceType } = this.parent;
        if (shippingServiceType === 'standard') {
          return !!value;
        }
        return true;
      }),
    shippingPartner: Yup.string(), // optional
    shippingId: Yup.string().required('Shipping ID is required'),
    maxLbPerPackage: Yup.string().test(
      'required-if-not-standard',
      'Required',
      function (value) {
        const { shippingServiceType } = this.parent;
        if (shippingServiceType !== 'standard') {
          return !!value;
        }
        return true;
      }
    ),
    minLbPerPackage: Yup.string().test(
      'required-if-not-standard',
      'Required',
      function (value) {
        const { shippingServiceType } = this.parent;
        if (shippingServiceType !== 'standard') {
          return !!value;
        }
        return true;
      }
    ),
    additionalLbPerPackage: Yup.string().test(
      'required-if-not-standard',
      'Required',
      function (value) {
        const { shippingServiceType } = this.parent;
        if (shippingServiceType !== 'standard') {
          return !!value;
        }
        return true;
      }
    ),
    rates: Yup.array().test(
      'required-if-not-standard',
      'Rates are required',
      function (value) {
        const { shippingServiceType } = this.parent;
        if (shippingServiceType !== 'standard') {
          // Must be a non-empty array and all objects must have required fields
          if (!Array.isArray(value) || value.length === 0) return false;
          return value.every(
            (rate) =>
              rate &&
              rate.minWeight !== undefined &&
              rate.maxWeight !== undefined &&
              rate.pricePerLb !== undefined &&
              rate.buyIn !== undefined
          );
        }
        return true;
      }
    ),
    baseRate: Yup.number().required('Required'),
    riskSurcharge: Yup.number().required('Required'),
    serviceFees: Yup.object({
      doorToDoorFee: Yup.number().required('Required'),
      pickUpFee: Yup.number().required('Required'),
    }),
    tracking: Yup.boolean(),
    maxDimension: Yup.object().test(
      'required-for-standard',
      'Maximum Dimensions are required for standard shipping',
      function (value) {
        const { shippingServiceType } = this.parent;
        if (shippingServiceType === 'standard') {
          return value &&
            value.L !== undefined && value.L !== '' &&
            value.W !== undefined && value.W !== '' &&
            value.H !== undefined && value.H !== '';
        }
        return true;
      }
    ),
    campaignMaturityDate: Yup.string().test(
      'required-if-not-standard',
      'Campaign Maturity Date is required',
      function (value) {
        const { shippingServiceType } = this.parent;
        if (shippingServiceType !== 'standard') {
          return !!value;
        }
        return true;
      }
    ),
    availableWeight: Yup.string().test(
      'required-if-crowdshipping',
      'Available Weight is required for CrowdShipping',
      function (value) {
        const { shippingServiceType } = this.parent;
        if (shippingServiceType === 'crowdshipping') {
          return !!value;
        }
        return true;
      }
    ),
  });
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      console.log(values, "values11111")
      setSubmitting(true);
      try {
        const computedRouteType = getRouteType(
          values.shippingServiceType,
          shippingServiceData.deliveryPartner,
          shippingServiceData.carrierPartner
        );
        if (shippingServiceData.id) {
          // Update Firestore and Redux if id exists (add or edit mode)
          const payload = {
            ...values,
            id: shippingServiceData.id,
            routeType: computedRouteType,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          if (values.shippingServiceType === 'standard') {
            delete payload.rates;
          } else {
            delete payload.pricePerPound;
          }
          const response = await createShippingService(payload);
          dispatch(setShippingServiceState({ ...shippingServiceData, ...values, id: response.id, updatedAt: response.updatedAt, routeType: computedRouteType }));
        }
        Success("Shipping price data saved successfully!");
        resetForm();
        navigate("/shipping/shipping-service");
      } catch (error) {
        Error("Error saving shipping price data: " + error.message);
      } finally {
        setSubmitting(false);
      }
    },
  });
  // Debug: log formik values
  console.log('formik.errors:', formik.errors);

  const { shippingServiceType, deliveryPartner, carrierPartner } = shippingServiceData;
  console.log(shippingServiceType, deliveryPartner, carrierPartner, "shippingServiceType, deliveryPartner, carrierPartner")
  const routeType = getRouteType(shippingServiceType, deliveryPartner, carrierPartner);

  useEffect(() => {
    const payload = {
      forwarder: "611180",
      fromCountry: "United States",
      fromState: "California",
      fromZipCode: "90001",
      toCountry: "India",
      toState: "Maharashtra",
      toCity: "Mumbai",
      toZipcode: "400001",
      Length: 10,
      Width: 10,
      Height: 10,
      Weight: 5,
      category: "Electronics",
      declaredValue: 100
    };
    const functions = getFunctions();
    const callableReturnMessage = httpsCallable(functions, "getAvailableShippingServices");
    callableReturnMessage(payload)
      .then(result => {
        console.log("CustomIQItemRestrictions res:", result, payload);
      })
      .catch(err => {
        console.error("CustomIQItemRestrictions error:", err);
      });
  }, []);

  if (loading) {
    return <Box sx={{ p: 4, textAlign: 'center' }}>Loading...</Box>;
  }

  return (
    <SimpleBar
      forceVisible="x"
      autoHide={true}
      style={{
        overflowX: "auto",
        maxHeight: "calc(100vh - 356px)",
        minHeight: "calc(100vh - 356)",
      }}
      className="custom-scrollbar"
    >
      <Box
        sx={{
          width: "50%",
          margin: "0 auto",
          padding: "20px",
          backgroundColor: "white",
        }}
      >
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <Box mb={3}>
              <Typography sx={{ fontWeight: 700, fontSize: '20px', mb: 1 }}>
                Shipping Service Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small" margin="normal">
                    <Typography sx={{ fontWeight: 500, fontSize: '16px', mb: 0.5 }}>
                      Shipping Partner
                    </Typography>
                    <Select
                      name="shippingPartner"
                      value={formik.values.shippingPartner}
                      onChange={formik.handleChange}
                    >
                      {shippingPartnerOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small" margin="normal">
                    <Typography sx={{ fontWeight: 500, fontSize: '16px', mb: 0.5 }}>
                      Shipping ID
                    </Typography>
                    <OutlinedInput
                      name="shippingId"
                      value={formik.values.shippingId}
                      onChange={formik.handleChange}
                      error={Boolean(formik.touched.shippingId && formik.errors.shippingId)}
                      placeholder="Enter Shipping ID"
                    />
                    {formik.touched.shippingId && formik.errors.shippingId && (
                      <Typography color="error" variant="caption">{formik.errors.shippingId}</Typography>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
            {/* CONDITIONAL: STANDARD TYPE */}
            {shippingServiceData.shippingServiceType === 'standard' ? (
              <>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: "20px" }}>
                    Price per pound
                  </Typography>
                  <FormControl sx={{ m: 1, minHeight: "35px", width: "23vw", marginX: "0px" }} size="small">
                    <OutlinedInput
                      type="number"
                      name="pricePerPound"
                      value={formik.values.pricePerPound}
                      onChange={formik.handleChange}
                      startAdornment={<InputAdornment position="start">$</InputAdornment>}
                    />
                  </FormControl>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: "20px", marginTop: 2 }}>
                    Volumetric Calculation
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: "20px", marginTop: 2 }}>
                    Maximum Dimensions
                  </Typography>
                  <Box display="flex" gap={2}>
                    <FormControl sx={{ m: 1, minHeight: "35px", width: "10vw", marginX: "0px" }} size="small">
                      <OutlinedInput
                        type="number"
                        name="maxDimension.L"
                        value={formik.values.maxDimension.L}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        startAdornment={<InputAdornment position="start">L</InputAdornment>}
                        placeholder="Length"
                        error={Boolean(formik.touched.maxDimension?.L && formik.errors.maxDimension)}
                      />
                    </FormControl>
                    <FormControl sx={{ m: 1, minHeight: "35px", width: "10vw", marginX: "0px" }} size="small">
                      <OutlinedInput
                        type="number"
                        name="maxDimension.W"
                        value={formik.values.maxDimension.W}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        startAdornment={<InputAdornment position="start">W</InputAdornment>}
                        placeholder="Width"
                        error={Boolean(formik.touched.maxDimension?.W && formik.errors.maxDimension)}
                      />
                    </FormControl>
                    <FormControl sx={{ m: 1, minHeight: "35px", width: "10vw", marginX: "0px" }} size="small">
                      <OutlinedInput
                        type="number"
                        name="maxDimension.H"
                        value={formik.values.maxDimension.H}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        startAdornment={<InputAdornment position="start">H</InputAdornment>}
                        placeholder="Height"
                        error={Boolean(formik.touched.maxDimension?.H && formik.errors.maxDimension)}
                      />
                    </FormControl>
                  </Box>
                  {formik.touched.maxDimension && formik.errors.maxDimension && (
                    <Typography color="error" variant="caption" sx={{ mt: 0.5, display: "block" }}>
                      {formik.errors.maxDimension}
                    </Typography>
                  )}
                </Box>
              </>
            ) : (
              <>
                <Box mb={3}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small" margin="normal">
                        <Typography sx={{ fontWeight: 500, fontSize: '16px', mb: 0.5 }}>
                          Campaign Maturity Date (CMD)
                        </Typography>
                        <OutlinedInput
                          type="date"
                          name="campaignMaturityDate"
                          value={formik.values.campaignMaturityDate || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="Select Campaign Maturity Date"
                          error={Boolean(formik.touched.campaignMaturityDate && formik.errors.campaignMaturityDate)}
                        />
                        {formik.touched.campaignMaturityDate && formik.errors.campaignMaturityDate && (
                          <Typography color="error" variant="caption" sx={{ mt: 0.5, display: "block" }}>
                            {formik.errors.campaignMaturityDate}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    {shippingServiceData.shippingServiceType === 'crowdshipping' && (
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small" margin="normal">
                          <Typography sx={{ fontWeight: 500, fontSize: '16px', mb: 0.5 }}>
                            Available Weight
                          </Typography>
                          <OutlinedInput
                            type="number"
                            name="availableWeight"
                            value={formik.values.availableWeight || ''}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            startAdornment={<InputAdornment position="start">lb</InputAdornment>}
                            placeholder="Enter Available Weight"
                            error={Boolean(formik.touched.availableWeight && formik.errors.availableWeight)}
                          />
                          {formik.touched.availableWeight && formik.errors.availableWeight && (
                            <Typography color="error" variant="caption" sx={{ mt: 0.5, display: "block" }}>
                              {formik.errors.availableWeight}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Grid>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: "20px" }}>
                    For each weight, enter cost & shipping per pound (lb)
                  </Typography>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ border: "1px solid #E5E5E8" }}></TableCell>
                        <TableCell
                          sx={{
                            color: "#959BA1",
                            fontWeight: 500,
                            border: "1px solid #E5E5E8",
                          }}
                        >
                          Min. Weight (lb)
                        </TableCell>
                        <TableCell
                          sx={{
                            color: "#959BA1",
                            fontWeight: 500,
                            border: "1px solid #E5E5E8",
                          }}
                        >
                          Max. Weight (lb)
                        </TableCell>
                        <TableCell
                          sx={{
                            color: "#959BA1",
                            fontWeight: 500,
                            border: "1px solid #E5E5E8",
                          }}
                        >
                          Price/lb ($)
                        </TableCell>
                        <TableCell
                          sx={{
                            color: "#959BA1",
                            fontWeight: 500,
                            border: "1px solid #E5E5E8",
                          }}
                        >
                          Buy-in
                        </TableCell>
                        <TableCell sx={{ border: "1px solid #E5E5E8" }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <FieldArray
                      name="rates"
                      render={(arrayHelpers) => (
                        <TableBody>
                          {formik.values.rates.map((rate, index) => (
                            <TableRow key={index}>
                              <TableCell sx={{ border: "1px solid #E5E5E8" }}>
                                {index + 1}
                              </TableCell>
                              <TableCell sx={{ border: "1px solid #E5E5E8" }}>
                                <TextField
                                  name={`rates[${index}].minWeight`}
                                  value={rate.minWeight}
                                  onChange={formik.handleChange}
                                  variant="standard"
                                  type="number"
                                  error={Boolean(
                                    formik.touched.rates?.[index]?.minWeight &&
                                    formik.errors.rates?.[index]?.minWeight
                                  )}
                                  helperText={
                                    formik.touched.rates?.[index]?.minWeight &&
                                    formik.errors.rates?.[index]?.minWeight
                                  }
                                  sx={{
                                    "& .MuiInput-underline:before": {
                                      borderBottom: "none",
                                    },
                                    "& .MuiInput-underline:after": {
                                      borderBottom: "none",
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ border: "1px solid #E5E5E8" }}>
                                <TextField
                                  name={`rates[${index}].maxWeight`}
                                  value={rate.maxWeight}
                                  onChange={formik.handleChange}
                                  variant="standard"
                                  type="number"
                                  error={Boolean(
                                    formik.touched.rates?.[index]?.maxWeight &&
                                    formik.errors.rates?.[index]?.maxWeight
                                  )}
                                  helperText={
                                    formik.touched.rates?.[index]?.maxWeight &&
                                    formik.errors.rates?.[index]?.maxWeight
                                  }
                                  sx={{
                                    "& .MuiInput-underline:before": {
                                      borderBottom: "none",
                                    },
                                    "& .MuiInput-underline:after": {
                                      borderBottom: "none",
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ border: "1px solid #E5E5E8" }}>
                                <TextField
                                  name={`rates[${index}].pricePerLb`}
                                  value={rate.pricePerLb}
                                  onChange={formik.handleChange}
                                  variant="standard"
                                  type="number"
                                  error={Boolean(
                                    formik.touched.rates?.[index]?.pricePerLb &&
                                    formik.errors.rates?.[index]?.pricePerLb
                                  )}
                                  helperText={
                                    formik.touched.rates?.[index]?.pricePerLb &&
                                    formik.errors.rates?.[index]?.pricePerLb
                                  }
                                  sx={{
                                    "& .MuiInput-underline:before": {
                                      borderBottom: "none",
                                    },
                                    "& .MuiInput-underline:after": {
                                      borderBottom: "none",
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ border: "1px solid #E5E5E8" }}>
                                <TextField
                                  name={`rates[${index}].buyIn`}
                                  value={rate.buyIn}
                                  onChange={formik.handleChange}
                                  variant="standard"
                                  type="number"
                                  error={Boolean(
                                    formik.touched.rates?.[index]?.buyIn &&
                                    formik.errors.rates?.[index]?.buyIn
                                  )}
                                  helperText={
                                    formik.touched.rates?.[index]?.buyIn &&
                                    formik.errors.rates?.[index]?.buyIn
                                  }
                                  sx={{
                                    "& .MuiInput-underline:before": {
                                      borderBottom: "none",
                                    },
                                    "& .MuiInput-underline:after": {
                                      borderBottom: "none",
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ border: "1px solid #E5E5E8" }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <IconButton
                                    onClick={() => arrayHelpers.insert(index + 1, { minWeight: "", maxWeight: "", pricePerLb: "", buyIn: "" })}
                                    size="small"
                                    color="primary"
                                    aria-label="add row"
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    onClick={() => arrayHelpers.remove(index)}
                                    size="small"
                                    color="error"
                                    aria-label="remove row"
                                    disabled={formik.values.rates.length === 1}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      )}
                    />
                  </Table>
                  <Typography sx={{ fontWeight: 500, fontSize: "17px", marginTop: 2 }}>
                    {getBuyInRateRange(formik.values.rates)}
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: "17px", marginTop: 2 }}>
                    {getCampaignMaturityDate(formik.values.rates)}
                  </Typography>
                </Box>
              </>
            )}
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: "20px", marginTop: 2 }}>
                Min lb/package
              </Typography>
              <FormControl sx={{ m: 1, minHeight: "35px", width: "23vw", marginX: "0px" }} size="small">
                <OutlinedInput
                  type="number"
                  name="minLbPerPackage"
                  value={formik.values.minLbPerPackage}
                  onChange={formik.handleChange}
                  startAdornment={<InputAdornment position="start">lb</InputAdornment>}
                />
              </FormControl>
            </Box>
            {/* END CONDITIONAL */}
            {/* Only show these for non-standard types */}
            {shippingServiceData.shippingServiceType !== 'standard' && (
              <>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: "20px", marginTop: 2 }}>
                    Max lb/package
                  </Typography>
                  <FormControl sx={{ m: 1, minHeight: "35px", width: "23vw", marginX: "0px" }} size="small">
                    <OutlinedInput
                      type="number"
                      name="maxLbPerPackage"
                      value={formik.values.maxLbPerPackage}
                      onChange={formik.handleChange}
                      startAdornment={<InputAdornment position="start">lb</InputAdornment>}
                    />
                  </FormControl>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: "20px", marginTop: 2 }}>
                    Additional lb/package
                  </Typography>
                  <FormControl sx={{ m: 1, minHeight: "35px", width: "23vw", marginX: "0px" }} size="small">
                    <OutlinedInput
                      type="number"
                      name="additionalLbPerPackage"
                      value={formik.values.additionalLbPerPackage}
                      onChange={formik.handleChange}
                      startAdornment={<InputAdornment position="start">$</InputAdornment>}
                    />
                  </FormControl>
                </Box>
                {/* Minimum Weight and Maximum Dimensions for non-standard types (already handled above) */}
              </>
            )}
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "20px", marginTop: 2 }}>
                Add Customs
              </Typography>
              <FormControl sx={{ width: "100%", mt: 1 }}>
                <Select
                  multiple
                  name="addCustoms"
                  value={formik.values.addCustoms}
                  onChange={formik.handleChange}
                  renderValue={(selected) => selected.join(", ")}
                >
                  {add_customs.map((item) => (
                    <MenuItem key={item} value={item}>
                      <Checkbox checked={formik.values.addCustoms.indexOf(item) > -1} />
                      <ListItemText primary={item} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {/* Insurance Details Grouping */}
            <Box sx={{ marginY: 2 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "20px", marginBottom: 2 }}>
                Insurance Details
              </Typography>
              <Box sx={{ display: 'flex', gap: 4 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: "16px", marginBottom: 1 }}>
                    Base Rate
                  </Typography>
                  <FormControl sx={{ minHeight: "35px", width: "100%" }} size="small">
                    <OutlinedInput
                      type="number"
                      name="baseRate"
                      value={formik.values.baseRate}
                      onChange={formik.handleChange}
                      startAdornment={<InputAdornment position="start">$</InputAdornment>}
                    />
                  </FormControl>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: "16px", marginBottom: 1 }}>
                    Risk Surcharge
                  </Typography>
                  <FormControl sx={{ minHeight: "35px", width: "100%" }} size="small">
                    <OutlinedInput
                      type="number"
                      name="riskSurcharge"
                      value={formik.values.riskSurcharge}
                      onChange={formik.handleChange}
                      startAdornment={<InputAdornment position="start">$</InputAdornment>}
                    />
                  </FormControl>
                </Box>
              </Box>
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "20px", marginTop: 2 }}>
                Service Fees
              </Typography>
              <Box display="flex" gap={2}>
                <FormControl sx={{ m: 1, minHeight: "35px", width: "23vw", marginX: "0px" }} size="small">
                  <OutlinedInput
                    type="number"
                    name="serviceFees.doorToDoorFee"
                    value={formik.values.serviceFees.doorToDoorFee}
                    onChange={formik.handleChange}
                    startAdornment={<InputAdornment position="start">$</InputAdornment>}
                    placeholder="Door-to-Door Fee"
                  />
                </FormControl>
                <FormControl sx={{ m: 1, minHeight: "35px", width: "23vw", marginX: "0px" }} size="small">
                  <OutlinedInput
                    type="number"
                    name="serviceFees.pickUpFee"
                    value={formik.values.serviceFees.pickUpFee}
                    onChange={formik.handleChange}
                    startAdornment={<InputAdornment position="start">$</InputAdornment>}
                    placeholder="Pick-up Fee"
                  />
                </FormControl>
              </Box>
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: "20px", marginTop: 2 }}>
                Tracking
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formik.values.tracking}
                    onChange={e => formik.setFieldValue("tracking", e.target.checked)}
                    name="tracking"
                    color="primary"
                  />
                }
                label={formik.values.tracking ? "Yes" : "No"}
              />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "20px",
                  marginTop: 2,
                }}
              >
                Route Type
              </Typography>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "16px",
                }}
              >
                {routeType !== "-" ? `Based on your selection your route type is ${routeType}` : "Route type will be determined after all selections."}
              </Typography>
            </Box>
          </form>
        </FormikProvider>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3, mt: 6 }}>
          <Button
            onClick={onBack}
            type="button"
            sx={{
              background: '#F4F5F7',
              color: '#A0A4AF',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '16px',
              px: 5,
              py: 1.5,
              boxShadow: 'none',
              textTransform: 'none',
              '&:hover': {
                background: '#E4E6EB',
                color: '#A0A4AF',
                boxShadow: 'none',
              },
            }}
            disabled={formik.isSubmitting}
          >
            Back
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              background: '#6C2BD7',
              color: '#fff',
              borderRadius: '8px',
              fontWeight: 700,
              fontSize: '16px',
              px: 5,
              py: 1.5,
              boxShadow: 'none',
              textTransform: 'none',
              '&:hover': {
                background: '#5A23B7',
                color: '#fff',
                boxShadow: 'none',
              },
            }}
            onClick={formik.handleSubmit}
            disabled={formik.isSubmitting}
          >
            Next
          </Button>
        </Box>
      </Box>
    </SimpleBar>
  );
}
